import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { MapComponent } from '@sembo-private/vue3-maps';
import { MapControl } from '@sembo-private/maps';
import type { MapMovedEvent, Map, Marker } from '@sembo-private/maps';

import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import { MapStateKey, TripStateKey } from '@apps/main/modules/state/state';
import { useEditingIngredientStore } from '@apps/main/modules/store/editingIngredientStore';
import { getImageUrl } from '@apps/main/modules/api/hotels';
import type { MarkerData } from '@apps/main/modules/state/mapState';
import { mapLanguageLocale } from '@apps/main/modules/localization/localeUtils';
import { MarkerType } from '@apps/main/modules/maps/markerFormatters';

import type { ActivityMapCardData, HighlightedActivity } from '../../models';
import { useActivitySelectionPanelsStore } from '../activitySelectionPanelsStore';

import {
  getActivityMapCardData,
  getActivitySelectionMarkers,
  getMarkerFormatters,
  getHighlightedActivity,
} from './activitySelectionMapStoreLogic';

type MapProperties = InstanceType<typeof MapComponent>['$props'];

export const useActivitySelectionMapStore = defineStore(
  'ActivitySelectionMap',
  () => {
    const mapState = injectStrict(MapStateKey);
    const tripState = injectStrict(TripStateKey);
    const editingIngredientStore = useEditingIngredientStore();
    const activitySelectionPanelsStore = useActivitySelectionPanelsStore();

    // State
    const isMapCreated = ref<boolean>(false);
    const isFirstMapRendered = ref<boolean>(true);
    const highlightedActivity = ref<HighlightedActivity | undefined>(undefined);

    // Actions
    function setDynamicMapIntegration(integration: Map): void {
      isFirstMapRendered.value = false;
      isMapCreated.value = true;
      mapState.setMapIntegration(integration);
    }

    function hightlightOnMap(activityCode: string | undefined): void {
      const ingredientId =
        editingIngredientStore.editingStayIngredient?.ingredientId;
      highlightedActivity.value = getHighlightedActivity(
        activityCode,
        ingredientId
      );
    }

    function markerClicked(marker: Marker): void {
      if (marker.useContentFormatter === MarkerType.Destination) {
        return;
      }
      const data = marker.data as MarkerData;
      highlightedActivity.value = {
        ingredientId: data.ingredientId,
        activityCode: data.hotelCode,
        showActivityMapCard: true,
      };
    }

    function toggleSearchByMovingMap(): void {
      mapState.searchByMapMovement.value = !mapState.searchByMapMovement.value;
    }

    function mapViewChanged(event: MapMovedEvent): void {
      mapState.setMapMovedAndCreateNewPolygon(event);
    }

    function hideActivityCard(): void {
      highlightedActivity.value = undefined;
    }

    function activityCardClicked(): void {
      if (!highlightedActivity.value || !activityMapCardData.value) {
        throw new Error('Not possible!');
      }

      const activityId = highlightedActivity.value;

      activitySelectionPanelsStore.openActivityDetailsPanel(
        activityId.activityCode,
        activityId.ingredientId
      );
    }

    function destroyMap(): void {
      mapState.mapShouldCenterOnMarkers.value = true;
    }

    // Getters
    const mapProperties = computed<MapProperties>(() => ({
      markers: getActivitySelectionMarkers(
        mapState.markers.value,
        highlightedActivity.value
      ),
      markerFormatters: getMarkerFormatters(),
      route: mapState.mapRoute.value,
      startRectangle: mapState.DEFAULT_RECTANGLE,
      gestureHandling: 'greedy',
      locale: mapLanguageLocale,
      mapControls: [MapControl.zoomControl],
      showAllMarkers: mapState.mapShouldCenterOnMarkers.value,
    }));

    const searchByMovingMap = computed<boolean>(
      () => mapState.searchByMapMovement.value
    );

    const activityMapCardData = computed<ActivityMapCardData | undefined>(() =>
      getActivityMapCardData(
        highlightedActivity.value,
        tripState.getStaticInfo,
        getImageUrl
      )
    );

    return {
      // State
      isMapCreated,
      highlightedActivity,

      // Actions
      setDynamicMapIntegration,
      hightlightOnMap,
      toggleSearchByMovingMap,
      markerClicked,
      mapViewChanged,
      hideActivityCard,
      activityCardClicked,
      destroyMap,

      // Getters
      mapProperties,
      searchByMovingMap,
      activityMapCardData,
    };
  }
);
